/**
 * MultiSelectView — Inspector panel shown when 2+ nodes are selected.
 *
 * Replaces the tabbed single-node inspector with a batch action panel.
 * Shows: selection count, risk summary, batch actions, address list.
 * Hard cap: MAX_BATCH_SIZE = 50 — over-limit disables all batch buttons.
 */
import React, { useState } from 'react';
import {
    X, Shield, FileText, Eye, Tag, Archive,
    AlertTriangle, Loader2, Combine
} from 'lucide-react';
import { clsx } from 'clsx';
import type { GraphNodeData } from '../../types/graph';
import { MAX_BATCH_SIZE } from '../../hooks/useMultiSelect';
import { FeatureExplainer } from '../ui/FeatureExplainer';
import { safeStr } from '../../utils/safeStr';
import { HolisticTraceCard } from './HolisticTraceCard';
import { useHolisticStore } from '../../stores/useHolisticStore';

interface MultiSelectViewProps {
    selectedNodes: Map<string, GraphNodeData>;
    count: number;
    isBatchOverLimit: boolean;
    riskSummary: { high: number; medium: number; low: number; unknown: number };
    onClose: () => void;
    onRemoveNode: (nodeId: string) => void;
    onBatchOFAC?: (addresses: { chain: string; address: string }[]) => void;
    onBatchNotice?: (addresses: string[]) => void;
    onBatchWatchlist?: (addresses: string[]) => void;
    onBatchLabel?: (addresses: string[]) => void;
    onBatchStash?: (nodeIds: string[]) => void;
    /** Archive a Holistic Trace report under the case/investigation (ISSUE-02). */
    onPersistHolisticReport?: (payload: { title: string; htmlContent: string; metadata?: Record<string, any> }) => Promise<void> | void;
}

const getRiskColor = (score?: number) => {
    if (score == null) return 'text-muted-foreground';
    if (score >= 0.7) return 'text-red-500';
    if (score >= 0.4) return 'text-amber-500';
    return 'text-green-500';
};

const getRiskDot = (score?: number) => {
    if (score == null) return 'bg-gray-400';
    if (score >= 0.7) return 'bg-red-500';
    if (score >= 0.4) return 'bg-amber-500';
    return 'bg-green-500';
};

const formatAddress = (addr: string) => {
    if (addr.length > 16) return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
    return addr;
};

export const MultiSelectView: React.FC<MultiSelectViewProps> = ({
    selectedNodes,
    count,
    isBatchOverLimit,
    riskSummary,
    onClose,
    onRemoveNode,
    onBatchOFAC,
    onBatchNotice,
    onBatchWatchlist,
    onBatchLabel,
    onBatchStash,
    onPersistHolisticReport,
}) => {
    const [loadingAction, setLoadingAction] = useState<string | null>(null);

    const nodes = Array.from(selectedNodes.values());
    const addresses = nodes.map(n => n.id);
    const primaryChain = nodes[0]?.chain || 'ETH'; // Default to first node's chain

    const handleAction = async (actionKey: string, fn?: (args: any) => void, args?: any) => {
        if (!fn || isBatchOverLimit) return;
        setLoadingAction(actionKey);
        try {
            await fn(args);
        } finally {
            setLoadingAction(null);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card shrink-0">
                <div>
                    <div className="text-sm font-semibold text-foreground">
                        {count} Nodes Selected
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                        {riskSummary.high > 0 && (
                            <span className="text-red-500">{riskSummary.high} High</span>
                        )}
                        {riskSummary.medium > 0 && (
                            <span className="text-amber-500">{riskSummary.medium} Medium</span>
                        )}
                        {riskSummary.low > 0 && (
                            <span className="text-green-500">{riskSummary.low} Low</span>
                        )}
                        {riskSummary.unknown > 0 && (
                            <span className="text-muted-foreground">{riskSummary.unknown} Unknown</span>
                        )}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-accent rounded-md text-muted-foreground hover:text-foreground"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto styled-scrollbar">
                {/* First-use explainer */}
                <div className="px-4 py-2">
                    <FeatureExplainer
                        featureKey="multi-select-batch"
                        title="Batch Actions"
                        what="Select multiple nodes on the graph (Ctrl+Click or drag-select) to perform bulk operations."
                        howItWorks="Batch OFAC checks all addresses against sanctions lists simultaneously. Batch stash hides nodes temporarily for a cleaner view."
                        interpret="Risk summary above shows aggregate risk scores. Actions are disabled if more than 50 nodes are selected."
                    />
                </div>

                {/* Over-limit warning */}
                {isBatchOverLimit && (
                    <div className="px-4 py-2.5 mx-4 mb-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 flex items-start gap-2 rounded-md">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <div className="text-xs text-amber-700 dark:text-amber-300">
                            <span className="font-medium">{count} nodes selected.</span> Batch actions
                            support max {MAX_BATCH_SIZE}. Refine your selection or use filters first.
                        </div>
                    </div>
                )}

                {/* Holistic Trace section */}
                {count >= 2 && (
                    <div className="px-4 py-2 mb-2">
                        <div className="border border-border rounded-lg bg-card overflow-hidden">
                            <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Combine className="w-4 h-4 text-primary" />
                                    <span className="text-sm font-semibold text-foreground">Holistic Multi-Node Trace</span>
                                </div>
                                <button
                                    onClick={() => useHolisticStore.getState().openModal(primaryChain, addresses)}
                                    disabled={isBatchOverLimit}
                                    className="px-3 py-1 text-xs font-medium bg-primary hover:bg-brand-gold-hover text-primary-foreground rounded transition-colors disabled:opacity-40"
                                >
                                    Run ({count})
                                </button>
                            </div>
                            <HolisticTraceCard onPersistReport={onPersistHolisticReport} />
                        </div>
                    </div>
                )}

                {/* Batch actions */}
                <div className="px-4 py-3 space-y-1.5 border-b border-border">
                    <div className="text-xs font-medium text-muted-foreground mb-2">Batch Actions</div>

                    {[
                        { key: 'ofac', label: 'Screen All for Sanctions', icon: Shield, fn: onBatchOFAC, args: nodes.map(n => ({ chain: n.chain || 'ETH', address: n.id })) },
                        { key: 'notice', label: 'Batch Subpoena Notices', icon: FileText, fn: onBatchNotice, args: addresses },
                        { key: 'watchlist', label: 'Add All to Watchlist', icon: Eye, fn: onBatchWatchlist, args: addresses },
                        { key: 'label', label: 'Apply Label to All', icon: Tag, fn: onBatchLabel, args: addresses },
                        { key: 'stash', label: 'Stash All from Graph', icon: Archive, fn: onBatchStash, args: addresses },
                    ].map(({ key, label, icon: Icon, fn, args }) => (
                        <button
                            key={key}
                            onClick={() => handleAction(key, fn, args)}
                            disabled={isBatchOverLimit || !fn || loadingAction !== null}
                            className={clsx(
                                "w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors text-left",
                                isBatchOverLimit || !fn
                                    ? "text-muted-foreground/50 cursor-not-allowed"
                                    : "text-foreground hover:bg-accent"
                            )}
                        >
                            {loadingAction === key ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Icon className="w-4 h-4 shrink-0" />
                            )}
                            {label}
                        </button>
                    ))}
                </div>

                {/* Address list */}
                <div className="px-4 py-3">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                        Selected Addresses ({count})
                    </div>
                    <div className="space-y-1">
                        {nodes.map(node => (
                            <div
                                key={node.id}
                                className="flex items-center justify-between gap-2 group px-2 py-1.5 rounded-md hover:bg-accent/50"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className={clsx("w-2 h-2 rounded-full shrink-0", getRiskDot(node.risk_score))} />
                                    <div className="min-w-0">
                                        <div className="text-xs font-mono truncate" title={node.id}>
                                            {formatAddress(node.id)}
                                        </div>
                                        {node.entity_name && (
                                            <div className="text-[10px] text-muted-foreground truncate">
                                                {safeStr(node.entity_name)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <span className={clsx("text-[10px] font-medium", getRiskColor(node.risk_score))}>
                                        {node.risk_score != null ? `${Math.round(node.risk_score * 100)}%` : '—'}
                                    </span>
                                    <button
                                        onClick={() => onRemoveNode(node.id)}
                                        className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-accent text-muted-foreground hover:text-foreground transition-opacity"
                                        title="Remove from selection"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
